//MIT License
//
//Part of this code is copyright (c) 2020 Devias
//
//Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
//The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
//THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
  makeStyles,
  Avatar
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import PersonIcon from '@material-ui/icons/Person';
import { useSelector, useDispatch } from "react-redux"

const PIItypes = ['CPR', 'PII', '.. swedens CPR'];

const useStyles = makeStyles((theme) => ({
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#fb741a',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.72rem, 0.9vw, 0.875rem)',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.72rem, 0.9vw, 0.875rem)',
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: '#FFFFFF',
    },
    '& .MuiSelect-selectMenu': {
      minHeight: 'unset !important',
      paddingTop: '10.5px',
      paddingBottom: '10.5px',
      lineHeight: 'normal',
      backgroundColor: "transparent",
    },
  },
  root:
   {
    boxShadow: "0 6px 32px rgba(0,45,85,0.18)",
    background: '#f7faff',
    borderRadius: 14,
  },
  Button: {
    color: "#FFFFFF",
    backgroundColor: "#002D55",
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    minHeight: 'clamp(38px, 3vw, 44px)',
    margin: 10,
    marginRight: 18,
    minWidth: 'clamp(170px, 15vw, 220px)',
    fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: "#fb741a",
      boxShadow: 'none',
    }
  },
  secondaryButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    minHeight: 'clamp(38px, 3vw, 44px)',
    margin: 10,
    marginLeft: 0,
    marginRight: 0,
    minWidth: 'clamp(170px, 15vw, 220px)',
    fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    }
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 30,
    width: 30,
    marginRight: 0,
    marginLeft: 0,
    marginTop: 0,
  },
  card: {
    background: '#f7faff', 
    borderRadius: 14,
    
  },
  cardContent: {
    background: '#f7faff', 
    borderRadius: 14,
    padding: '12px 24px 24px 16px !important',
  },
  cardHeaderRoot: {
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    padding: 'clamp(8px, calc(1px + 0.55vw), 12px) clamp(10px, calc(2px + 0.625vw), 16px)',
    boxSizing: 'border-box',
    borderBottom: '2px solid #FF7F27',
    '& .MuiCardHeader-content': {
      overflow: 'hidden',
    },
    '& .MuiCardHeader-title': {
      fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)',
      fontWeight: 700,
      color: '#002D55',
      lineHeight: 1.2,
    },
  },
}));

const ProfileDetails = ({
  className,
  selectedPatient,
  headerTabs,
  headerTitle = 'Patient details',
  onProfileSaved,
  ...rest
}) => {
  const classes = useStyles();
  const [token, _] = useState(useSelector(state => state.auth.token))


  // Local state for each field
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [cpr, setCpr] = useState('');
  const [country, setCountry] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [insurance, setInsurance] = useState('');
  const [piiType, setPiiType] = useState('CPR');
  const [piiNumber, setPiiNumber] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const populateFormValues = (patient) => {
    setFirstName(patient?.first_name || '');
    setLastName(patient?.last_name || '');
    setEmail(patient?.email || '');
    setPhone(patient?.phone || '');
    setBirthdate(patient?.birthdate || '');
    setAddress(patient?.address || '');
    setCity(patient?.city || '');
    setZip(patient?.zip || '');
    setCpr(patient?.cpr || '');
    setCountry(patient?.country || '');
    setPaymentStatus(patient?.payment_status || '');
    setPaymentMethod(patient?.payment_method || '');
    setInsurance(patient?.insurance || '');
    setPiiType(patient?.pii_type || 'CPR');
    setPiiNumber(patient?.pii_number || '');
  };

  // Populate state from selectedPatient on mount or when selectedPatient changes
  useEffect(() => {
    populateFormValues(selectedPatient);
  }, [selectedPatient]);

  const handleCancel = () => {
    populateFormValues(selectedPatient);
  };

  const saveHandler = async () => {
    if (!selectedPatient || !selectedPatient.user_id) {
      alert("No patient selected");
      return;
    }
    const data = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      birthdate: birthdate,
      address: address,
      city: city,
      zip: zip,
      cpr: cpr,
      country: country,
      payment_status: paymentStatus,
      payment_method: paymentMethod,
      insurance: insurance,
      pii_type: piiType,
      pii_number: piiNumber,
    };
    try {
      const response = await fetch(`https://api.virtus-one.com/profile/update/${selectedPatient.user_id}/`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          "Authorization": `Token ${token}`
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        setSnackbarOpen(true);
        if (onProfileSaved) {
          onProfileSaved(data);
        }
      } else {
        alert("Failed to update patient details.");
      }
    } catch (e) {
      alert("Error updating patient details: " + e.message);
    }
  }

  return (
    <>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <MuiAlert elevation={6} variant="filled" severity="success" onClose={() => setSnackbarOpen(false)}>
        Patient details saved successfully.
      </MuiAlert>
    </Snackbar>
    <form
      autoComplete="off"
      noValidate
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeaderRoot}
          //avatar={<Avatar className={classes.avatar}><PersonIcon /></Avatar>}
          title={
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
              {headerTitle ? (
                <span style={{ color: "#002D55", fontWeight: "bold", fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', paddingLeft: 5 }}>Patient</span>
              ) : null}
              {headerTabs ? (
                <Box display="flex" alignItems="center">
                  {headerTabs}
                </Box>
              ) : null}
            </Box>
          }
          style={{ marginBottom: 0, alignItems: 'center' }}
        />
        <Divider style={{marginLeft: 0}}/>
        <CardContent className={classes.cardContent}>
          <Typography style={{ paddingLeft: 8, fontWeight: 600, color: '#002D55', marginBottom: 16, fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }}>
            Personal Information
          </Typography>
          <Typography  className={classes.bodyText} style={{ marginLeft: 8, color: "#666666", marginBottom: 'clamp(16px, 0.78vw, 24px)', fontSize: 'clamp(0.76rem, 0.81vw, 0.9375rem)'  }}>
            Add or edit insurance payer information, and add or edit insurance referral letters.
          </Typography>
          <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="First name" variant="outlined" value={firstName} onChange={e => setFirstName(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Last name" variant="outlined" value={lastName} onChange={e => setLastName(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Email" variant="outlined" value={email} onChange={e => setEmail(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Phone" variant="outlined" value={phone} onChange={e => setPhone(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Address" variant="outlined" value={address} onChange={e => setAddress(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="City" variant="outlined" value={city} onChange={e => setCity(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="ZIP" variant="outlined" value={zip} onChange={e => setZip(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="Country" variant="outlined" value={country} onChange={e => setCountry(e.target.value)} />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none"
                select
                fullWidth
                label="PII Type"
                variant="outlined"
                value={piiType}
                onChange={e => setPiiType(e.target.value)}
              >
                <MenuItem value="CPR">CPR</MenuItem>
                <MenuItem value="PII">PII</MenuItem>
                <MenuItem value=".. swedens CPR">.. swedens CPR</MenuItem>
              </TextField>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField className={classes.orangeFocused} size="small" margin="none" fullWidth label="PII Number" variant="outlined" value={piiNumber} onChange={e => setPiiNumber(e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <Box
          display="flex"
          justifyContent="flex-end"
          p={2}
          style={{ gap: 8 }}
        >
          <Button
            className={classes.Button}
            variant="contained"
            fullWidth
            style={{ flex: 1, marginRight: 0 }}
            onClick={saveHandler}
          >
            Save details
          </Button>
          <Button
            className={classes.secondaryButton}
            variant="outlined"
            fullWidth
            style={{ flex: 1 }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Card>
    </form>
    </>
  );
};

ProfileDetails.propTypes = {
  className: PropTypes.string
};

export default ProfileDetails;